import { cloneDeep, cloneDeepWith } from 'lodash';
import isURL from 'validator/es/lib/isURL';
import isUUID from 'validator/es/lib/isUUID';
import isEmail from 'validator/es/lib/isEmail';
import isNumeric from 'validator/es/lib/isNumeric';
import isBoolean from 'validator/es/lib/isBoolean';
import {
  assignValue,
  findMatch,
  hasKey,
  hasMinimumLength,
  hasPlainObject,
  isEmpty,
  isPlainObject,
  parseUuid, removeDuplicates,
  removeEmpty,
  replaceEmptyString,
  stringToCheck,
} from '@/utils/resultsHelper';
import parseDate from '@/utils/dateHelper';
import { translatedError, indexOfError } from '@/utils/errors';
import { isSplitUrl, joinUrl, splitUrl } from '@/constants/urls';
import fieldNames, { fieldLengths } from '@/constants/fields';
import formFieldTypes, {
  editableFormFieldTypes,
  formFieldFormats,
  formFieldArrays,
  formFieldSimpleArrays,
  formObjectArrays,
  includesFieldType,
} from '../constants';

export const isFormUriField = (validation) => (validation?.format === formFieldFormats.URI);

export const isFormEmailField = (validation) => (validation?.format === formFieldFormats.EMAIL);

export const isFormUuidField = (validation) => (validation?.format === formFieldFormats.UUID);

export const isFormMultiplesField = (validation) => (validation?.multiple);

const applySimpleFormDefaultValue = (
  parent,
  name,
  type,
  validation,
  defaultValue,
) => {
  if (isFormUriField(validation)) {
    assignValue(parent, name, splitUrl(defaultValue));
  } else if (isFormUuidField(validation)) {
    assignValue(parent, name, parseUuid(defaultValue));
  } else if (includesFieldType(formFieldArrays, type) || isFormMultiplesField(validation)) {
    assignValue(parent, name, []);
  } else if (type === formFieldTypes.DATE.type) {
    assignValue(parent, name, parseDate(defaultValue));
  } else if (type !== formFieldTypes.LABEL.type) {
    assignValue(parent, name, hasMinimumLength(defaultValue) ? defaultValue : null);
  }
};

export const safeFormObjects = (items) => {
  if (isEmpty(items) || Array.isArray(items)) {
    return items;
  }

  if (isPlainObject(items) && Object.keys(items).length > 0) {
    return [items];
  }

  return [];
};

export const safeFormArray = (items) => {
  if (Array.isArray(items)) {
    return items;
  }

  if (!isEmpty(items) && !isPlainObject(items)) {
    return [items];
  }

  return [];
};

export const formFieldConversionError = (entity, name, message = 'data') => {
  const error = translatedError(entity, `form.invalid.section.${entity}`, `form.field.convert.${message}`, [name]);
  error.warning = true;
  return error;
};

export const formFieldRemovedError = (missing) => {
  const error = translatedError('server', 'form.fields.removed.title', 'form.fields.removed.text');
  error.details = missing;
  error.warning = true;
  return error;
};

const checkForArrays = (parent, { name, validation, type }, errors, entity) => {
  const valueIsArray = Array.isArray(parent[name]);
  const valueShouldBeArray = includesFieldType(formFieldSimpleArrays, type)
    || (type === formFieldTypes.LIST.type && isFormMultiplesField(validation));

  if (valueIsArray && !valueShouldBeArray) {
    errors.push(formFieldConversionError(entity, name, 'many.to.one'));
    const value = [
      formFieldTypes.MD.type,
      formFieldTypes.STRING.type,
      formFieldTypes.TEXT.type,
    ].includes(type) ? parent[name].join(', ') : parent[name][0];
    assignValue(parent, name, value);
  } else if (!valueIsArray && valueShouldBeArray) {
    errors.push(formFieldConversionError(entity, name, 'many.to.one'));
    assignValue(parent, name, [parent[name]]);
  }
};

const applyFormNonObjectDefaultValue = (parent, {
  name,
  validation,
  type,
  defaultValue,
}, errors, entity) => {
  if (!hasKey(parent, name)) {
    applySimpleFormDefaultValue(parent, name, type, validation, defaultValue);
  } else if (isPlainObject(parent[name]) || hasPlainObject(parent[name])) {
    errors.push(formFieldConversionError(entity, name, 'complex.to.simple'));
    applySimpleFormDefaultValue(parent, name, type, validation, defaultValue);
  } else if ([formFieldTypes.STRING.type].includes(type) && isFormUriField(validation)) {
    assignValue(parent, name, splitUrl(parent[name]));
  } else {
    checkForArrays(parent, { name, validation, type }, errors, entity);
  }
};

const alignFormObjectToTemplate = (parent, name, fields, errors, entity) => {
  const names = fields.map((field) => field.name);
  const redundant = removeEmpty(Object.keys(parent[name]).map((n) => (names.includes(n) ? null : n)));

  if (redundant.length > 0) {
    const error = formFieldConversionError(entity, name, 'fields.removed');
    error.details = redundant;
    errors.push(error);
    redundant.forEach((m) => assignValue(parent[name], m, null));
  }
};

const applyFormObjectDefaultValue = (parent, name, fields, errors, entity) => {
  const isNew = !hasKey(parent, name);
  if (isNew) {
    assignValue(parent, name, {});
  } else if (Array.isArray(parent[name])) {
    errors.push(formFieldConversionError(entity, name, 'many.to.one'));
    assignValue(parent, name, parent[name][0]);
  } else if (!isEmpty(parent[name]) && !isPlainObject(parent[name])) {
    errors.push(formFieldConversionError(entity, name, 'to.object'));
    assignValue(parent, name, {});
  }

  alignFormObjectToTemplate(parent, name, fields, errors, entity);
  fields.forEach((f) => applyFormNonObjectDefaultValue(parent[name], f, errors, entity));
};

const applyFormObjectsDefaultValue = (parent, name, errors, entity) => {
  const isNew = !hasKey(parent, name);
  if (isNew) {
    assignValue(parent, name, []);
  } else if (isPlainObject(parent[name])) {
    errors.push(formFieldConversionError(entity, name, 'one.to.many'));
    const copy = [cloneDeep(parent[name])];
    assignValue(parent, name, copy);
  } else if (!isEmpty(parent[name]) && !Array.isArray(parent[name])) {
    errors.push(formFieldConversionError(entity, name, 'to.objects'));
    assignValue(parent, name, []);
  }
};

const applyFieldAttributes = (field, name, type = 'string', format = null) => {
  const copy = cloneDeep(field);
  copy.name = name;
  copy.type = type;

  if (format) {
    if (!hasKey(field, 'validation')) { copy.validation = {}; }
    if (!hasMinimumLength(field?.validation?.format)) { copy.validation.format = format; }
  }

  return copy;
};

export const linkFields = (field) => {
  const fields = [applyFieldAttributes(field.text, 'text'),
    applyFieldAttributes(field.link, 'link', 'string', 'uri')];

  if (hasKey(field, 'target')) {
    fields.push(applyFieldAttributes(field.target, 'target'));
  }

  return fields;
};

export const identifyRedundantFields = (parent, fields) => {
  const names = [...fields, fieldNames.TITLE.toLowerCase(), fieldNames.DESCRIPTION.toLowerCase()];
  return removeEmpty(Object.keys(parent).map((name) => (names.includes(name) ? null : name)));
};

const replaceValue = (parent, redundant, keys, source, target) => {
  const replaced = redundant.includes(source) && !keys.includes(target);
  if (replaced) { assignValue(parent, target, parent[source]); }

  return replaced;
};

const convertObjectToLink = (parent, name, errors, entity) => {
  const keys = Object.keys(parent[name]);
  const redundant = removeEmpty(keys.map((n) => (['text', 'link', 'target'].includes(n) ? null : n)));
  const replaced = [{ source: 'name', target: 'text' }, { source: 'url', target: 'link' }].map(({ source, target }) => (
    replaceValue(parent[name], redundant, keys, source, target)
  ));

  if (replaced.includes(true)) { errors.push(formFieldConversionError(entity, name, 'to.link')); }
  redundant.forEach((m) => assignValue(parent[name], m, null));
};

const applyFormLinkDefaultValue = (parent, field, errors, entity) => {
  const value = parent[field.name];
  if (isPlainObject(value)) {
    convertObjectToLink(parent, field.name, errors, entity);
  } else if (value && isURL(value)) {
    errors.push(formFieldConversionError(entity, field.name, 'to.link'));
    assignValue(parent, field.name, { link: splitUrl(value) });
  }
  applyFormObjectDefaultValue(parent, field.name, linkFields(field), errors, entity);
};

const applyFormDefaultValue = (parent, field, errors, entity) => {
  if (field.type === formFieldTypes.OBJECT.type) {
    applyFormObjectDefaultValue(parent, field.name, field.fields, errors, entity);
  } else if (field.type === formFieldTypes.LINK.type) {
    applyFormLinkDefaultValue(parent, field, errors, entity);
  } else if (includesFieldType(formObjectArrays, field.type)) {
    applyFormObjectsDefaultValue(parent, field.name, errors, entity);
  } else {
    applyFormNonObjectDefaultValue(parent, field, errors, entity);
  }
};

export const applyFormDefaultValues = (sections, parent) => {
  const names = [];
  const errors = [];

  Object.keys(sections).forEach((section) => {
    sections[section].fields.forEach((field) => {
      names.push(field.name);
      applyFormDefaultValue(parent, field, errors, section);
    });
  });

  return { names, errors };
};

const valueTransform = (value) => (isSplitUrl(value) ? joinUrl(value) : replaceEmptyString(value));

export const transformFormObjects = (items) => cloneDeepWith(items, valueTransform);

export const transformForm = (parent) => cloneDeepWith(parent, valueTransform);

const newFormFieldLink = (field) => {
  const item = {};
  const errors = [];
  linkFields(field).forEach((f) => applyFormDefaultValue(item, f, errors, ''));

  return item;
};

const newFormFieldObject = ({ fields }) => {
  const item = {};
  const errors = [];
  fields.forEach((field) => applyFormDefaultValue(item, field, errors, ''));

  return item;
};

const assignFormObjectsFieldsEditProps = (fields, items) => {
  const errors = [];

  items.forEach((item, index) => {
    if (item) {
      fields.forEach((field) => applyFormDefaultValue(item, field, errors, index.toString()));
    }
  });

  return errors;
};

const emptyKeys = (item) => removeEmpty(Object.keys(item).map((key) => (isEmpty(item[key]) ? key : undefined)));

const crushObject = (item) => {
  const keys = emptyKeys(item);
  if (keys.length === 0) { return item; }
  if (keys.length === Object.keys(item).length) { return undefined; }

  const copy = cloneDeep(item);
  keys.forEach((key) => delete copy[key]);
  return copy;
};

const crushObjectsArray = (parent, key, items) => (isPlainObject(items[0])
  ? removeEmpty(parent[key].map((child) => crushObject(child)))
  : removeDuplicates(items, true));

const crushArray = (parent, key) => {
  const sanitised = removeEmpty(parent[key]);
  const value = sanitised.length === 0 ? undefined : crushObjectsArray(parent, key, sanitised);
  assignValue(parent, key, value);
};

export const crushFormProps = (form) => {
  const copy = transformForm(form);
  Object.keys(copy).forEach((key) => {
    if (isPlainObject(copy[key])) {
      assignValue(copy, key, crushObject(copy[key]));
    } else if (Array.isArray(copy[key])) {
      crushArray(copy, key);
    }
  });

  emptyKeys(copy).forEach((key) => delete copy[key]);
  return copy;
};

export const findFormSectionByFieldName = ({ sections }, fieldName) => (
  Object.keys(sections).find((name) => (findMatch(sections[name].fields, fieldName, 'name')))
);

export const findFormFieldByName = ({ sections }, fieldName) => {
  const [field] = removeEmpty(Object.keys(sections).map((name) => (
    findMatch(sections[name].fields, fieldName, 'name'))));

  return field;
};

export const assignFormLinksEditProps = (form, name, items) => {
  const field = findFormFieldByName(form, name);
  return assignFormObjectsFieldsEditProps(field ? linkFields(field) : [], items);
};

export const assignFormObjectsEditProps = (form, name, items) => {
  const field = findFormFieldByName(form, name);
  return assignFormObjectsFieldsEditProps(field ? field.fields : [], items);
};

export const newFormLink = (form, name, isEditing) => (
  isEditing ? newFormFieldLink(findFormFieldByName(form, name)) : { }
);

export const newFormObject = (form, name, isEditing) => (
  isEditing ? newFormFieldObject(findFormFieldByName(form, name)) : {}
);

export const alignFieldsToForm = (parent, { sections }) => {
  const { names, errors } = applyFormDefaultValues(sections, parent);
  const redundant = identifyRedundantFields(parent, names);

  if (redundant.length > 0) {
    errors.push(formFieldRemovedError(redundant));
    redundant.forEach((m) => assignValue(parent, m, null));
  }

  return { errors, redundant };
};

const filteredFields = (parent, sections, allowedList = editableFormFieldTypes) => {
  const allFields = Object.values(sections).map(({ fields }) => fields).flat();
  return ({ fields: allFields.filter((f) => allowedList.includes(f.type)), values: parent?.fields || {} });
};

export const unknownFieldValues = (parent, sections) => {
  const values = {};
  const items = filteredFields(parent, sections);
  const names = items.fields.map((field) => field.name);
  const keys = Object.keys(items.values).filter((key) => !names.includes(key));

  keys.forEach((key) => { values[key] = items.values[key]; });

  return values;
};

const unknownFieldTemplate = (name, type = formFieldTypes.STRING.type) => ({
  name, label: name, type, validation: {},
});

const unknownLinkTemplate = (name, type = formFieldTypes.LINK.type) => ({
  ...unknownFieldTemplate(name, type),
  text: unknownFieldTemplate(null, formFieldTypes.STRING.type),
  link: { ...unknownFieldTemplate(null), validation: { format: formFieldFormats.URI } },
  target: unknownFieldTemplate(null, formFieldTypes.STRING.type),
});

const simpleUnknownField = (name, value) => {
  if (isNumeric(value)) {
    return unknownFieldTemplate(name, formFieldTypes.NUMBER.type);
  } if (isBoolean(value)) {
    return unknownFieldTemplate(name, formFieldTypes.BOOL.type);
  } if (isEmail(value)) {
    return { ...unknownFieldTemplate(name), validation: { format: formFieldFormats.EMAIL } };
  } if (isURL(value)) {
    return { ...unknownFieldTemplate(name), validation: { format: formFieldFormats.URI } };
  } if (isUUID(value)) {
    return { ...unknownFieldTemplate(name), validation: { format: formFieldFormats.UUID } };
  } if (hasMinimumLength(value, fieldLengths.large)) {
    return unknownFieldTemplate(name, formFieldTypes.MD.type);
  }
  return unknownFieldTemplate(name);
};

const isLinkField = (value) => {
  const keys = isPlainObject(value) ? Object.keys(value) : [];
  return keys.every((k) => ['text', 'link', 'target'].includes(k)) && keys.includes('link');
};

const unknownField = (name, value) => {
  if (isLinkField(value)) {
    return unknownLinkTemplate(name);
  } if (isPlainObject(value)) {
    return {
      ...unknownFieldTemplate(name, formFieldTypes.OBJECT.type),
      fields: Object.keys(value).map((key) => unknownField(key, value[key])),
    };
  }
  if (hasPlainObject(value) && value.every(isLinkField)) {
    return unknownLinkTemplate(name, formFieldTypes.LINKS.type);
  }
  if (hasPlainObject(value)) {
    return {
      ...unknownFieldTemplate(name, formFieldTypes.OBJECTS.type),
      fields: Object.keys(value[0]).map((key) => unknownField(key, value[0][key])),
    };
  }
  if (Array.isArray(value)) {
    return unknownFieldTemplate(name, formFieldTypes.STRINGS.type);
  }

  return simpleUnknownField(name, stringToCheck(value));
};

export const unknownFields = (values) => Object.keys(values).map((name) => unknownField(name, values[name]));

export const removeUnknownFieldValues = (parent, sections, additionalFields = []) => {
  const unknown = [...additionalFields, ...Object.keys(unknownFieldValues(parent, sections))];
  const fields = { ...parent.fields };

  unknown.forEach((key) => {
    delete fields[key];
  });

  return fields;
};

export const pruneFormFieldErrors = (errors, { fields, entity }) => {
  let index = indexOfError(errors, entity, fields);
  if (index < 0) {
    return errors;
  }

  const copy = cloneDeep(errors);
  while (index >= 0) {
    copy.splice(index, 1);
    index = indexOfError(copy, entity, fields);
  }

  return copy;
};

export const formSections = (names = ['first', 'second']) => {
  const sections = { sections: {} };
  names.forEach((name) => {
    sections.sections[name] = { label: '', fields: [] };
  });

  return sections;
};

export default applyFormDefaultValue;
